import { useEffect, useId, useRef, useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";

import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";
import {
  searchClientCandidates,
  searchPropertyCandidates,
  unwrapPagedResults,
} from "../api/candidateSearch";
import { createCalendarTask, updateCalendarTask } from "../api/taskMutations";
import { fetchPropertySummary } from "../../tasksList/api/properties";
import { propertyTitleOrFallback } from "../../tasksList/lib/taskFormatting";
import { CALENDAR_STATUS_OPTIONS } from "../lib/calendarStatusLabels";
import { trapTabKey } from "../lib/focusTrap";
import {
  EDITABLE_KEYS,
  PRIORITY_OPTIONS,
  buildCreatePayload,
  buildPatchPayload,
  draftFromTask,
  emptyCreateDraft,
  validateCalendarDraft,
} from "../lib/taskEditorCodec";

const SEARCH_DEBOUNCE_MS = 300;

function CalendarTaskEditorModal({
  open,
  mode,
  task,
  prefillDay,
  propertyLabel: propertyLabelProp = "",
  fixedDealId = null,
  onClose,
  onSaved,
  mutationGenerationRef,
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const submittingRef = useRef(false);
  const clientSearchGenRef = useRef(0);
  const propertySearchGenRef = useRef(0);
  const clientControllerRef = useRef(null);
  const propertyControllerRef = useRef(null);

  const fixedDealCreateMode =
    mode === "create" && fixedDealId != null && fixedDealId !== "";

  const [draft, setDraft] = useState(() => emptyCreateDraft(prefillDay));
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitState, setSubmitState] = useState("idle");

  const [clientSearch, setClientSearch] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoadState, setClientLoadState] = useState("idle");
  const [propertySearch, setPropertySearch] = useState("");
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoadState, setPropertyLoadState] = useState("idle");

  useEffect(() => {
    if (!open) return undefined;
    const nextDraft =
      mode === "edit" && task
        ? draftFromTask(task, { propertyLabel: propertyLabelProp })
        : emptyCreateDraft(prefillDay);
    setDraft(nextDraft);
    setFieldErrors({});
    setFormError(null);
    setSubmitState("idle");
    submittingRef.current = false;
    setClientSearch("");
    setPropertySearch("");
    setClientOptions([]);
    setPropertyOptions([]);
    setClientLoadState("idle");
    setPropertyLoadState("idle");
    return undefined;
  }, [open, mode, task, prefillDay, propertyLabelProp]);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const timer = window.setTimeout(() => {
      const input = dialogRef.current?.querySelector("#calendar-task-title");
      if (input && typeof input.focus === "function") input.focus();
    }, 0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (submittingRef.current) return;
        onClose();
        return;
      }
      trapTabKey(event, dialogRef.current);
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === "function"
      ) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  // Bounded targeted Property summary when editing a linked property
  // whose readable title is not yet known from the parent cache.
  useEffect(() => {
    if (!open || mode !== "edit" || !task?.property) return undefined;
    const propertyId = task.property;
    const looksLikeFallback =
      !propertyLabelProp ||
      propertyLabelProp === propertyTitleOrFallback(null, propertyId);
    if (!looksLikeFallback && propertyLabelProp) {
      // Parent already supplied a real title — keep it.
      return undefined;
    }
    const controller = new AbortController();
    fetchPropertySummary(propertyId, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        const label = propertyTitleOrFallback(data, propertyId);
        setDraft((prev) => {
          if (String(prev.propertyId) !== String(propertyId)) return prev;
          if (prev.propertyLabel && prev.propertyLabel !== propertyTitleOrFallback(null, propertyId)) {
            return prev;
          }
          return { ...prev, propertyLabel: label };
        });
      })
      .catch(() => {
        // Keep fallback label; visibility failures stay privacy-safe.
      });
    return () => controller.abort();
  }, [open, mode, task, propertyLabelProp]);

  useEffect(() => {
    if (!open || fixedDealCreateMode) return undefined;
    const timer = window.setTimeout(() => {
      clientSearchGenRef.current += 1;
      const generation = clientSearchGenRef.current;
      if (clientControllerRef.current) {
        clientControllerRef.current.abort();
      }
      const controller = new AbortController();
      clientControllerRef.current = controller;
      setClientLoadState("loading");
      searchClientCandidates(
        { search: clientSearch },
        { signal: controller.signal },
      )
        .then((data) => {
          if (generation !== clientSearchGenRef.current) return;
          setClientOptions(unwrapPagedResults(data));
          setClientLoadState("loaded");
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (generation !== clientSearchGenRef.current) return;
          setClientOptions([]);
          setClientLoadState("error");
          setFormError((prev) => prev || messageFromError(err));
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      if (clientControllerRef.current) {
        clientControllerRef.current.abort();
      }
    };
  }, [open, clientSearch, fixedDealCreateMode]);

  useEffect(() => {
    if (!open || fixedDealCreateMode) return undefined;
    const timer = window.setTimeout(() => {
      propertySearchGenRef.current += 1;
      const generation = propertySearchGenRef.current;
      if (propertyControllerRef.current) {
        propertyControllerRef.current.abort();
      }
      const controller = new AbortController();
      propertyControllerRef.current = controller;
      setPropertyLoadState("loading");
      searchPropertyCandidates(
        { search: propertySearch },
        { signal: controller.signal },
      )
        .then((data) => {
          if (generation !== propertySearchGenRef.current) return;
          setPropertyOptions(unwrapPagedResults(data));
          setPropertyLoadState("loaded");
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (generation !== propertySearchGenRef.current) return;
          setPropertyOptions([]);
          setPropertyLoadState("error");
          setFormError((prev) => prev || messageFromError(err));
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      if (propertyControllerRef.current) {
        propertyControllerRef.current.abort();
      }
    };
  }, [open, propertySearch, fixedDealCreateMode]);

  if (!open) return null;

  const submitting = submitState === "submitting";
  const heading = mode === "edit" ? "Редагувати задачу" : "Нова задача";

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field] && !prev._form) return prev;
      const out = { ...prev };
      delete out[field];
      delete out._form;
      return out;
    });
    setFormError(null);
  };

  const handleFieldChange = (field) => (event) => {
    const next = event.target.value;
    setDraft((prev) => ({ ...prev, [field]: next }));
    clearFieldError(field === "clientId" ? "client_id" : field === "propertyId" ? "property" : field);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitting || submittingRef.current) return;

    const localErrors = validateCalendarDraft(draft);
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setFormError("Перевірте заповнення форми.");
      return;
    }

    submittingRef.current = true;
    setSubmitState("submitting");
    setFieldErrors({});
    setFormError(null);

    const generationAtStart = mutationGenerationRef?.current;

    try {
      let saved;
      if (mode === "edit" && task) {
        const patch = buildPatchPayload(draft, task);
        if (Object.keys(patch).length === 0) {
          setSubmitState("idle");
          submittingRef.current = false;
          onClose();
          return;
        }
        saved = await updateCalendarTask(task.id, patch);
      } else {
        saved = await createCalendarTask(
          buildCreatePayload(draft, {
            fixedDealId: fixedDealCreateMode ? fixedDealId : null,
          }),
        );
      }

      if (
        mutationGenerationRef != null &&
        generationAtStart !== mutationGenerationRef.current
      ) {
        setSubmitState("idle");
        submittingRef.current = false;
        onClose();
        return;
      }

      setSubmitState("saved");
      submittingRef.current = false;
      onSaved(saved, { mode });
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, EDITABLE_KEYS);
      setFieldErrors(parsed);
      setFormError(parsed._form || messageFromError(err));
      setSubmitState("error");
      submittingRef.current = false;
    }
  };

  const clientDisplay =
    draft.clientLabel ||
    (draft.clientId
      ? clientOptions.find((c) => String(c.id) === String(draft.clientId))
      : null);
  const propertyDisplay =
    draft.propertyLabel ||
    (draft.propertyId
      ? propertyOptions.find((p) => String(p.id) === String(draft.propertyId))
      : null);

  return (
    <div
      className="feature-tasks-calendar__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="feature-tasks-calendar__modal-backdrop"
        aria-hidden="true"
        onClick={() => {
          if (!submittingRef.current) onClose();
        }}
      />
      <div className="feature-tasks-calendar__modal-dialog" ref={dialogRef}>
        <header className="feature-tasks-calendar__modal-head">
          <h2 id={titleId} className="feature-tasks-calendar__modal-title">
            <i className="fa-solid fa-list-check" aria-hidden="true" />
            <span>{heading}</span>
          </h2>
          <button
            type="button"
            className="feature-tasks-calendar__modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Закрити редактор задачі"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <form
          className="ui-form feature-tasks-calendar__modal-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="feature-tasks-calendar__modal-body">
            <div className="ui-form__rows">
              <Field
                id="calendar-task-title"
                label="Назва"
                required
                error={fieldErrors.title}
              >
                <TextInput
                  id="calendar-task-title"
                  value={draft.title}
                  onChange={handleFieldChange("title")}
                  maxLength={255}
                  disabled={submitting}
                  aria-required="true"
                />
              </Field>

              <Field
                id="calendar-task-description"
                label="Опис"
                error={fieldErrors.description}
              >
                <Textarea
                  id="calendar-task-description"
                  value={draft.description}
                  onChange={handleFieldChange("description")}
                  rows={3}
                  disabled={submitting}
                />
              </Field>

              <div className="feature-tasks-calendar__form-grid">
                <Field
                  id="calendar-task-due"
                  label="Дедлайн"
                  required
                  error={fieldErrors.due_date}
                >
                  <TextInput
                    id="calendar-task-due"
                    type="datetime-local"
                    value={draft.due_date}
                    onChange={handleFieldChange("due_date")}
                    disabled={submitting}
                    aria-required="true"
                  />
                </Field>

                <Field
                  id="calendar-task-priority"
                  label="Пріоритет"
                  error={fieldErrors.priority}
                >
                  <Select
                    id="calendar-task-priority"
                    value={draft.priority}
                    onChange={handleFieldChange("priority")}
                    options={PRIORITY_OPTIONS}
                    disabled={submitting}
                  />
                </Field>

                <Field
                  id="calendar-task-status"
                  label="Статус"
                  error={fieldErrors.status}
                >
                  <Select
                    id="calendar-task-status"
                    value={draft.status}
                    onChange={handleFieldChange("status")}
                    options={CALENDAR_STATUS_OPTIONS}
                    disabled={submitting}
                  />
                </Field>
              </div>

              {!fixedDealCreateMode ? (
                <>
              <fieldset className="feature-tasks-calendar__candidate-fieldset">
                <legend>Клієнт (необов&apos;язково)</legend>
                <label className="visually-hidden" htmlFor="calendar-task-client-search">
                  Пошук клієнта
                </label>
                <TextInput
                  id="calendar-task-client-search"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Пошук клієнта"
                  disabled={submitting}
                  aria-label="Пошук клієнта"
                />
                {clientLoadState === "loading" ? (
                  <Spinner size="sm" label="Завантаження клієнтів" />
                ) : null}
                <div
                  className="feature-tasks-calendar__candidate-list"
                  role="group"
                  aria-label="Кандидати клієнтів"
                >
                  <button
                    type="button"
                    aria-pressed={!draft.clientId}
                    className="feature-tasks-calendar__candidate-option"
                    onClick={() => {
                      setDraft((prev) => ({
                        ...prev,
                        clientId: "",
                        clientLabel: "",
                      }));
                      clearFieldError("client_id");
                    }}
                    disabled={submitting}
                  >
                    Без клієнта
                  </button>
                  {clientOptions.map((client) => {
                    const label =
                      client.fullname ||
                      [client.first_name, client.last_name]
                        .filter(Boolean)
                        .join(" ") ||
                      client.email ||
                      `Клієнт #${client.id}`;
                    const selected = String(draft.clientId) === String(client.id);
                    return (
                      <button
                        key={client.id}
                        type="button"
                        aria-pressed={selected}
                        className="feature-tasks-calendar__candidate-option"
                        onClick={() => {
                          setDraft((prev) => ({
                            ...prev,
                            clientId: String(client.id),
                            clientLabel: label,
                          }));
                          clearFieldError("client_id");
                        }}
                        disabled={submitting}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {draft.clientId ? (
                  <p className="feature-tasks-calendar__candidate-selected">
                    Обрано:{" "}
                    {draft.clientLabel ||
                      (typeof clientDisplay === "string"
                        ? clientDisplay
                        : clientDisplay?.fullname ||
                          [clientDisplay?.first_name, clientDisplay?.last_name]
                            .filter(Boolean)
                            .join(" ") ||
                          `Клієнт #${draft.clientId}`)}
                  </p>
                ) : null}
                {fieldErrors.client_id ? (
                  <InlineError>{fieldErrors.client_id}</InlineError>
                ) : null}
              </fieldset>

              <fieldset className="feature-tasks-calendar__candidate-fieldset">
                <legend>Об&apos;єкт (необов&apos;язково)</legend>
                <label className="visually-hidden" htmlFor="calendar-task-property-search">
                  Пошук об&apos;єкта
                </label>
                <TextInput
                  id="calendar-task-property-search"
                  value={propertySearch}
                  onChange={(e) => setPropertySearch(e.target.value)}
                  placeholder="Пошук об'єкта"
                  disabled={submitting}
                  aria-label="Пошук об'єкта"
                />
                {propertyLoadState === "loading" ? (
                  <Spinner size="sm" label="Завантаження об'єктів" />
                ) : null}
                <div
                  className="feature-tasks-calendar__candidate-list"
                  role="group"
                  aria-label="Кандидати об'єктів"
                >
                  <button
                    type="button"
                    aria-pressed={!draft.propertyId}
                    className="feature-tasks-calendar__candidate-option"
                    onClick={() => {
                      setDraft((prev) => ({
                        ...prev,
                        propertyId: "",
                        propertyLabel: "",
                      }));
                      clearFieldError("property");
                    }}
                    disabled={submitting}
                  >
                    Без об&apos;єкта
                  </button>
                  {propertyOptions.map((property) => {
                    const label =
                      property.title || `Об'єкт #${property.id}`;
                    const selected =
                      String(draft.propertyId) === String(property.id);
                    return (
                      <button
                        key={property.id}
                        type="button"
                        aria-pressed={selected}
                        className="feature-tasks-calendar__candidate-option"
                        onClick={() => {
                          setDraft((prev) => ({
                            ...prev,
                            propertyId: String(property.id),
                            propertyLabel: label,
                          }));
                          clearFieldError("property");
                        }}
                        disabled={submitting}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {draft.propertyId ? (
                  <p className="feature-tasks-calendar__candidate-selected">
                    Обрано:{" "}
                    {draft.propertyLabel ||
                      (typeof propertyDisplay === "string"
                        ? propertyDisplay
                        : propertyDisplay?.title || `Об'єкт #${draft.propertyId}`)}
                  </p>
                ) : null}
                {fieldErrors.property ? (
                  <InlineError>{fieldErrors.property}</InlineError>
                ) : null}
              </fieldset>
                </>
              ) : fieldErrors.deal_id ? (
                <InlineError>{fieldErrors.deal_id}</InlineError>
              ) : null}
            </div>

            {formError ? (
              <p
                className="feature-tasks-calendar__mutation-error"
                role="alert"
                aria-live="assertive"
              >
                {formError}
              </p>
            ) : null}
          </div>

          <div className="feature-tasks-calendar__modal-actions">
            <button
              type="button"
              className="action-btn"
              onClick={onClose}
              disabled={submitting}
            >
              Скасувати
            </button>
            <button
              type="submit"
              className="action-btn action-btn--primary"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? (
                <Spinner size="sm" label="Збереження" />
              ) : mode === "edit" ? (
                "Зберегти"
              ) : (
                "Створити"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CalendarTaskEditorModal;
